import {
  buildChecklistInspectionLevels,
  isCheckpointCountedAsPassedInLevelCounter,
} from "./buildChecklistInspectionLevels";
import { normalizeBadgeLevel } from "./normalizeBadgeLevel";

const COACH_INSPECT_LEVELS = ["Apprentice", "Professional", "Coach", "Master"];

const LEVEL_NUM = {
  novice: 0,
  apprentice: 1,
  professional: 2,
  proficient: 2,
  coach: 3,
  master: 4,
  architect: 4,
};

/**
 * Maps employee current badge label to 0–4 (Novice–Architect).
 */
export function resolveCoachBadgeLevelNumber(level) {
  if (level == null || level === "") return 0;
  if (typeof level === "number") {
    if (level >= 1 && level <= 4) return level;
    return 0;
  }
  const normalized = String(level).trim().toLowerCase();
  return LEVEL_NUM[normalized] ?? 0;
}

/**
 * True when every lower tier that has checkpoints is fully passed.
 * Empty lower tiers do not block awarding a higher tier.
 */
function areLowerCoachLevelsCompleteForAward(group, allGroups) {
  const levelNum = LEVEL_NUM[String(group?.level ?? "").toLowerCase()] ?? 0;
  if (!levelNum || !Array.isArray(allGroups) || !allGroups.length) return true;

  return allGroups.every((other) => {
    const otherNum = LEVEL_NUM[String(other?.level ?? "").toLowerCase()] ?? 0;
    if (otherNum <= 0 || otherNum >= levelNum) return true;
    const total = Number(other?.total) || 0;
    if (total === 0) return true;
    return Number(other?.passed) >= total;
  });
}

/**
 * Per-level badge button for the coach checklist accordion.
 * Award only when this tier and all lower tiers with checkpoints are complete.
 */
export function resolveCoachBadgeAction(group, currentBadgeLevel, allGroups) {
  const total = Number(group?.total) || 0;
  const passed = Number(group?.passed) || 0;
  const levelNum = LEVEL_NUM[String(group?.level ?? "").toLowerCase()] ?? 0;
  const currentNum = resolveCoachBadgeLevelNumber(currentBadgeLevel);

  if (levelNum === 0) return null;
  if (currentNum >= levelNum) return null;

  if (total === 0) {
    return "Give Badge";
  }

  if (passed === total && areLowerCoachLevelsCompleteForAward(group, allGroups)) {
    return "Award";
  }

  return "Give Badge";
}

/**
 * Checklist rows for a tier (and below) are all passed — coach may award that tier.
 */
export function isCoachChecklistLevelCompleteForAward(level, items) {
  const targetNum = LEVEL_NUM[String(level ?? "").toLowerCase()];
  if (!targetNum || !Array.isArray(items) || !items.length) return false;

  const relevant = items.filter((item) => {
    const fromProficiency =
      LEVEL_NUM[
        ["", "apprentice", "professional", "coach", "master"][Number(item?.proficiency_level) || 0]
      ];
    const fromReadiness = LEVEL_NUM[String(item?.readiness_level ?? "").toLowerCase()] ?? 0;
    const itemNum = fromProficiency || fromReadiness;
    return itemNum > 0 && itemNum <= targetNum;
  });

  if (!relevant.length) return false;
  return relevant.every((item) => isCheckpointCountedAsPassedInLevelCounter(item));
}

function formatCoachBadgeDot(status) {
  if (!status) return "No Dot";
  const normalized = String(status).toLowerCase().replace(/\s+/g, "_");
  const map = {
    good_standing: "Good Standing",
    given: "Given",
    obsolete: "Obsolete",
    no_dot: "No Dot",
  };
  return map[normalized] || status;
}

function normalizeCoachBadgeLevelLabel(level) {
  if (level == null || level === "") return null;
  if (typeof level === "number") {
    return ["", "Apprentice", "Professional", "Coach", "Master"][level] || null;
  }
  const s = String(level).trim();
  if (!s || s.toLowerCase() === "novice") return null;
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

/**
 * Badge chip data for the tier row when the employee currently holds that level.
 */
export function resolveCoachAwardedBadgeForGroup(group, currentBadgeLevel, currentBadge) {
  if (!currentBadge || !group?.level) return undefined;
  const groupNum = LEVEL_NUM[String(group.level).toLowerCase()] ?? 0;
  const badgeNum = resolveCoachBadgeLevelNumber(currentBadgeLevel);
  if (!groupNum || groupNum !== badgeNum) return undefined;

  const levelLabel =
    normalizeCoachBadgeLevelLabel(currentBadge.level) ||
    normalizeCoachBadgeLevelLabel(currentBadgeLevel);
  if (!levelLabel) return undefined;

  return {
    id: currentBadge.id ?? null,
    level: levelLabel,
    dot: formatCoachBadgeDot(currentBadge.status ?? currentBadge.dot),
    expiration_date: currentBadge.expiration_date ?? null,
    expires_in_days_from_becoming_obsolete:
      currentBadge.expires_in_days_from_becoming_obsolete ?? null,
    has_expiration_if_obsolete: !!currentBadge.has_expiration_if_obsolete,
    retries_to_expire_in_days: currentBadge.retries_to_expire_in_days ?? undefined,
  };
}

/**
 * Visible accordion tiers based on the logged-in user's current badge
 * for this responsibility.
 * Coach → hide Coach + top tier.
 * Architect + architectWillCoach → hide Coach + top tier.
 * Architect without coaching → hide the top tier only.
 * The tier names stay the legacy checklist labels: buildChecklistInspectionLevels owns them.
 */
export function resolveCoachInspectorVisibleLevels(loggedInUserBadgeLevel, architectWillCoach) {
  const level = normalizeBadgeLevel(String(loggedInUserBadgeLevel ?? ""));

  if (level === "coach") {
    return COACH_INSPECT_LEVELS.filter((name) => name !== "Coach" && name !== "Master");
  }

  if (level === "architect") {
    if (architectWillCoach === true) {
      return COACH_INSPECT_LEVELS.filter((name) => name !== "Coach" && name !== "Master");
    }
    return COACH_INSPECT_LEVELS.filter((name) => name !== "Master");
  }

  return [...COACH_INSPECT_LEVELS];
}

/**
 * Maps employee checklist API rows into AsCoachChecklistInspectionTable `levels`,
 * including per-tier badge actions (Award / Give Badge) and awarded badge chips.
 */
export function buildCoachChecklistInspectionLevels(items, options = {}) {
  const {
    currentBadgeLevel = "Novice",
    currentBadge = null,
    loggedInUserBadgeLevel,
    loggedInUserArchitectWillCoach,
    ...levelOptions
  } = options;

  const visibleLevels =
    loggedInUserBadgeLevel != null && String(loggedInUserBadgeLevel).trim() !== ""
      ? resolveCoachInspectorVisibleLevels(loggedInUserBadgeLevel, loggedInUserArchitectWillCoach)
      : levelOptions.levels;

  const groups = buildChecklistInspectionLevels(items, {
    ...levelOptions,
    levels: visibleLevels,
  });

  return groups.map((group) => {
    const badgeAction = resolveCoachBadgeAction(group, currentBadgeLevel, groups);
    const awardedBadge = resolveCoachAwardedBadgeForGroup(group, currentBadgeLevel, currentBadge);
    return {
      ...group,
      badgeAction: badgeAction || undefined,
      awardedBadge: awardedBadge || undefined,
    };
  });
}
